import { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Authentication layout components
import CustomBasicLayout from "layouts/auth/components/CustomBasicLayout";

import AuthService from "services/auth-service";
import { AuthContext } from "context";

function Register() {
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();

  const [inputs, setInputs] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    agree: false,
  });

  const [errors, setErrors] = useState({
    firstNameError: false,
    lastNameError: false,
    emailError: false,
    passwordError: false,
    agreeError: false,
    error: false,
    errorText: "",
  });

  const changeHandler = (e) => {
    setInputs({ ...inputs, [e.target.name]: e.target.value });
  };

  const handleAgreeChange = (e) => {
    setInputs({ ...inputs, agree: e.target.checked });
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    const mailFormat = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;

    if (inputs.firstName.trim().length === 0) {
      setErrors({ ...errors, firstNameError: true });
      return;
    }
    if (inputs.lastName.trim().length === 0) {
      setErrors({ ...errors, lastNameError: true });
      return;
    }
    if (inputs.email.trim().length === 0 || !inputs.email.trim().match(mailFormat)) {
      setErrors({ ...errors, emailError: true });
      return;
    }
    if (inputs.password.trim().length < 8) {
      setErrors({ ...errors, passwordError: true });
      return;
    }
    if (!inputs.agree) {
      setErrors({ ...errors, agreeError: true });
      return;
    }

    const newUser = {
      firstName: inputs.firstName,
      lastName: inputs.lastName,
      email: inputs.email,
      password: inputs.password,
    };

    try {
      const response = await AuthService.register(newUser);
      // Instead of logging in directly, redirect to activation message page
      navigate("/activation-message");
    } catch (err) {
      // Handle specific error for user already exists
      if (err.response && err.response.status === 409 && err.response.data && err.response.data.description === "User already exists.") {
        setErrors({ ...errors, error: true, errorText: "An account with this email already exists. Please use a different email or sign in instead." });
      } else {
        setErrors({ ...errors, error: true, errorText: err.message || "An error occurred during registration. Please try again." });
      }
    }
  };

  const inputStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 3,
      backgroundColor: "#f8f9fa",
      "& fieldset": { borderColor: "#e0e0e0" },
      "&:hover fieldset": { borderColor: "#465475" },
      "&.Mui-focused fieldset": { borderColor: "#465475", borderWidth: 2 },
    },
  };

  return (
    <CustomBasicLayout>
      <Card sx={{ maxWidth: 420, width: "100%", p: 4, borderRadius: 3 }}>
        {/* Logo/Icon */}
        <MDBox display="flex" flexDirection="column" alignItems="center" mb={3}>
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="center"
            width={80}
            height={80}
            borderRadius="50%"
            sx={{
              background: "linear-gradient(135deg, #5a6a94 0%, #465475 100%)",
              boxShadow: "0 4px 20px rgba(70, 84, 117, 0.3)",
              mb: 2,
            }}
          >
            <Icon sx={{ fontSize: 36, color: "white" }}>person_add</Icon>
          </MDBox>
          <MDTypography variant="h4" fontWeight="bold" color="dark">
            Create Account
          </MDTypography>
          <MDTypography variant="body2" color="text" sx={{ mt: 0.5 }}>
            Start managing your finances today
          </MDTypography>
        </MDBox>

        {/* Form */}
        <MDBox component="form" onSubmit={submitHandler}>
          {/* First Name */}
          <MDBox mb={2}>
            <MDTypography variant="button" fontWeight="medium" color="text" display="block" mb={1}>
              First Name
            </MDTypography>
            <TextField
              fullWidth
              name="firstName"
              value={inputs.firstName}
              onChange={changeHandler}
              error={errors.firstNameError}
              helperText={errors.firstNameError ? "First name cannot be empty" : ""}
              sx={inputStyles}
            />
          </MDBox>

          {/* Last Name */}
          <MDBox mb={2}>
            <MDTypography variant="button" fontWeight="medium" color="text" display="block" mb={1}>
              Last Name
            </MDTypography>
            <TextField
              fullWidth
              name="lastName"
              value={inputs.lastName}
              onChange={changeHandler}
              error={errors.lastNameError}
              helperText={errors.lastNameError ? "Last name cannot be empty" : ""}
              sx={inputStyles}
            />
          </MDBox>

          {/* Email */}
          <MDBox mb={2}>
            <MDTypography variant="button" fontWeight="medium" color="text" display="block" mb={1}>
              Email Address
            </MDTypography>
            <TextField
              fullWidth
              type="email"
              name="email"
              placeholder="you@example.com"
              value={inputs.email}
              onChange={changeHandler}
              error={errors.emailError}
              helperText={errors.emailError ? "Please enter a valid email" : ""}
              sx={inputStyles}
            />
          </MDBox>

          {/* Password */}
          <MDBox mb={2}>
            <MDTypography variant="button" fontWeight="medium" color="text" display="block" mb={1}>
              Password
            </MDTypography>
            <TextField
              fullWidth
              type="password"
              name="password"
              placeholder="Min. 8 characters"
              value={inputs.password}
              onChange={changeHandler}
              error={errors.passwordError}
              helperText={errors.passwordError ? "Password must be at least 8 characters" : ""}
              sx={inputStyles}
            />
          </MDBox>

          {/* Terms and Conditions */}
          <MDBox display="flex" alignItems="center" mb={2}>
            <Checkbox
              checked={inputs.agree}
              onChange={handleAgreeChange}
              sx={{
                color: "#465475",
                "&.Mui-checked": { color: "#465475" },
              }}
            />
            <MDTypography variant="button" fontWeight="regular" color="text">
              I agree to the Terms and Conditions
            </MDTypography>
          </MDBox>
          {errors.agreeError && (
            <MDTypography variant="caption" color="error" display="block" mb={2}>
              You must agree to the Terms and Conditions
            </MDTypography>
          )}

          {/* Error Message */}
          {errors.error && (
            <MDBox mb={2} p={1.5} borderRadius={2} sx={{ backgroundColor: "#fee2e2" }}>
              <MDTypography variant="caption" color="error" fontWeight="medium">
                {errors.errorText}
              </MDTypography>
            </MDBox>
          )}

          {/* Sign Up Button */}
          <MDButton
            variant="gradient"
            color="info"
            fullWidth
            size="large"
            type="submit"
            sx={{
              borderRadius: 3,
              py: 1.5,
              textTransform: "none",
              fontSize: "1rem",
            }}
          >
            Create Account
          </MDButton>

          {/* Sign In Link */}
          <MDBox mt={3} textAlign="center">
            <MDTypography variant="button" color="text">
              Already have an account?{" "}
              <MDTypography
                component={Link}
                to="/login"
                variant="button"
                color="info"
                fontWeight="medium"
              >
                Sign in
              </MDTypography>
            </MDTypography>
          </MDBox>
        </MDBox>
      </Card>
    </CustomBasicLayout>
  );
}

export default Register;